import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { ArrowRight, Magnifier, Persons, Comments } from "@gravity-ui/icons";
import { useGetMysteriesQuery } from "../../reducers/apiSlice";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import CaseSpread from "../../components/lightbox/CaseSpread";
import RolePreview from "../../components/lightbox/RolePreview";
import CaseFileBox from "../../components/lightbox/CaseFileBox";
import StoryRail from "../../components/lightbox/StoryRail";
import Loupe from "../../components/lightbox/Loupe";
import TypeReview from "../../components/lightbox/TypeReview";
import { initialTypeface } from "../../components/lightbox/typefaces";
import type { Typeface } from "../../components/lightbox/typefaces";
import { HOW_CASE } from "../../components/lightbox/howCase";
import SlideMount from "../../components/lightbox/SlideMount";
import { FALLBACK_CASES, toCaseView } from "../../components/lightbox/caseView";
import type { CaseView } from "../../components/lightbox/caseView";
import { useStoryMode } from "../../components/lightbox/useStoryMode";
import { frameFor, heroFrame } from "../../components/lightbox/frames";
import styles from "./Home.module.scss";

/* UI-DESIGN: LB-14 the Home is a scroll story over one lit lightbox
   (docs/lightbox/STORY.md): the hero over the whole box, each case slide
   in turn with its glass card, beginning with Case 00 / How it works,
   and a last Host a party. Every
   chapter is a normal section in reading order; the three.js scene (when
   the visitor gets it) only draws behind them. Data, routes and login are
   Avi's, unchanged. */

const CASES_ON_HOME = 3;

/* UI-DESIGN: LB-48 the whole night in four steps, in Taco's words: pick,
   set up and invite, play by live texts, and the host plays too. Short
   titles, one line each (DRAFT for Avi) */
const STEPS = [
  { title: "Pick a case", text: "Find the mystery that fits your crew and how many people are coming." },
  { title: "Invite your friends", text: "Name the party and send the link. Everyone RSVPs from their phone." },
  { title: "Live texts all night", text: "Updates about your character land on your phone as the night goes on." },
  { title: "You play too", text: "The host gets a character too, so nobody has to sit out and run the game." },
] as const;

/* UI-DESIGN: LB-58 what you receive, in plain words (Avi: name the
   suspects, the clues and how people play). One line each. */
const GETS = [
  { title: "Suspects", text: "Everyone gets a character with their own backstory, plus a few secrets about the people next to them." },
  { title: "Clues", text: "The night plays out in rounds, and every round drops new clues on everyone's phone." },
  { title: "Questioning", text: "Then you grill each other until somebody slips up." },
] as const;

const Home = () => {
  const navigate = useNavigate();
  const direction = new URLSearchParams(window.location.search).get("home-direction");
  const directionStudy = direction === "editorial" || direction === "sans";
  const requestedMode = useStoryMode();
  const [typeface, setTypeface] = useState(initialTypeface);
  const reviewType = new URLSearchParams(window.location.search).has("type");
  const chooseTypeface = (value: Typeface) => {
    setTypeface(value);
    const url = new URL(window.location.href);
    url.searchParams.set("type", value);
    history.replaceState(null, "", url);
  };
  const [sceneFailed, setSceneFailed] = useState(false);
  const mode = requestedMode === "three" && sceneFailed ? "static" : requestedMode;
  const { data: mysteries, isLoading, isFetching, isError, refetch } = useGetMysteriesQuery(undefined);

  const liveCases: CaseView[] = (mysteries ?? []).slice(0, CASES_ON_HOME).map(toCaseView);
  /* UI-DESIGN: local fallback only when the query errors, and labelled */
  const cases = isError ? FALLBACK_CASES : liveCases;

  const [chapter, setChapter] = useState(0);
  /* UI-DESIGN: LB-15 the picked-up slide (the old selected state), and
     the slide under the cursor for the loupe */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const sceneUnavailable = useCallback(() => {
    setSceneFailed(true);
    setSelectedId(null);
  }, []);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const pending = useRef<string | null>(null);
  const sceneCases = [HOW_CASE, ...cases];
  const hovered = sceneCases.find((c) => c.id === hoverId) ?? null;
  /* UI-DESIGN: LB-23 chapters run hero, How it works, then the cases;
     LB-58 the case file sits between the hero and How; LB-59 the three
     cases share one chapter: hero 0, file 1, How 2, cases 3, Host 4 */
  const CASES_CHAPTER = 3;
  const chapterOf = (id: string) => (id === HOW_CASE.id ? 2 : sceneCases.some((c) => c.id === id) ? CASES_CHAPTER : -1);

  /* picking a slide: if it is the case on screen, pick it up (or put it
     back); if not, glide to its chapter first and pick it up there */
  const pick = (id: string) => {
    if (chapterOf(id) === chapter) {
      setSelectedId((cur) => (cur === id ? null : id));
      return;
    }
    const view = sceneCases.find((c) => c.id === id);
    if (!view) return;
    pending.current = id;
    setSelectedId(null);
    document.getElementById(view.id === HOW_CASE.id ? "how-it-works" : "cases")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onChapter = (index: number) => {
    setChapter(index);
    if (pending.current && chapterOf(pending.current) === index) {
      setSelectedId(pending.current);
      pending.current = null;
      return;
    }
    // scrolling away from a picked slide puts it back
    setSelectedId((cur) => (cur && chapterOf(cur) !== index ? null : cur));
  };

  /* UI-DESIGN: Escape puts the slide back and returns focus to the card's
     Look closer button */
  const putBack = useCallback(() => {
    const id = selectedId;
    setSelectedId(null);
    if (id) {
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(`[data-case-id="${CSS.escape(id)}"] [data-lb-look]`)?.focus();
      });
    }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") putBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, putBack]);

  /* UI-DESIGN: LB-45 the case whose preview is open (LB-77: a role), if any */
  const [previewing, setPreviewing] = useState<CaseView | null>(null);

  const hostCase = (view: CaseView) => {
    /* UI-DESIGN: preview records are illustrations, never real cases to
       pass into the hosting form, even if a stale click reaches here. */
    if (isError || !liveCases.some((c) => c.id === view.id)) return;
    navigate(`/create-game?mystery=${encodeURIComponent(view.id)}`);
  };

  const rail = [
    { id: "top", label: "Top" },
    { id: "inside", label: "File" },
    { id: "how-it-works", label: "How" },
    { id: "cases", label: "Cases" },
    { id: "host", label: "Host" },
  ];

  const goTo = (id: string, fallback: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else navigate(fallback);
  };

  /* UI-DESIGN: LB-19 the sticky notes, arrows and magnifier prop are gone:
     the loupe over the slides is the one prop that does a job.
     LB-43 (Taco 2026-10-04: "sensitive, don't shill"): the panel says what
     this is, a case file you buy once and host at home, in the headline
     and one subline (LB-50: the eyebrow is gone). LB-48 the typed fact line is gone
     (Taco). No price on the page until Avi sets one.
     DRAFT for Taco/Avi: both CTA labels
     (docs/lightbox/lb43-46/COPY.md). */
  const heroCopy = (
    <>
      {/* UI-DESIGN: LB-48 Taco's headline and subline, word for word.
          LB-50 no eyebrow (Taco): the headline opens the panel */}
      <h1 id="lb-hero-title" className={clsx(styles.title, styles.heroTitle)}>
        A Murder Mystery kit waiting for you.
      </h1>
      <p className={clsx(styles.sub, styles.heroSub)}>
        Host a murder mystery with your friends that will continue to change as the night progresses.
      </p>
      <div className={styles.actions}>
        <LightboxButton
          tone="primary"
          trailing={<ArrowRight />}
          onPress={() => goTo(cases.length ? "cases" : "how-it-works", "/mysteries")}
        >
          Choose a case
        </LightboxButton>
        <LightboxButton tone="secondary" onPress={() => goTo("inside", "/about")}>
          What you get
        </LightboxButton>
      </div>
    </>
  );

  return (
    <LightboxSurface
      mode={mode}
      className={clsx(!new URLSearchParams(window.location.search).has("mobile-original") && styles.mobileCleanup, directionStudy && styles.directionStudy, direction === "editorial" && styles.editorialStudy, direction === "sans" && styles.sansStudy)}
      typeface={typeface}
      cases={isLoading ? [HOW_CASE] : sceneCases}
      selectedId={selectedId}
      onChapter={onChapter}
      onPick={pick}
      onHover={setHoverId}
      onUnavailable={sceneUnavailable}
    >
      <main className={styles.home} data-lb-story="">
        {/* UI-DESIGN: LB-24 `data-lb-active` marks the chapter the scene is
            on; in the live scene the other chapters' cards step back, so
            one card reads at a time on the shorter scroll */}
        <section
          id="top"
          className={styles.heroChapter}
          data-lb-chapter="hero"
          data-lb-active={chapter === 0 ? "" : undefined}
          aria-labelledby="lb-hero-title"
        >
          {/* UI-DESIGN: LB-16 phones: a pre-rendered frame of the scene's
              opening shot sits behind the hero instead of live WebGL */}
          {mode === "frames" ? <img className={styles.heroFrame} src={heroFrame} alt="" fetchPriority="high" /> : null}
          <div className={styles.heroLayer}>
            {/* UI-DESIGN: LB-42 the copy sits on a known module, a
                hard-edged smoked-glass panel (Taco picked it over a split
                column), on desktop and over the phones' pre-rendered frame */}
            <GlassPanel className={clsx(styles.card, styles.heroCopy, styles.heroPanel)}>{heroCopy}</GlassPanel>
          </div>
          {/* UI-DESIGN: LB-18 static stack: one case large, two peeking in */}
          {mode !== "frames" && cases.length > 0 ? (
            <div className={styles.heroStage} aria-hidden>
              {cases.slice(0, 3).map((view) => (
                <SlideMount key={view.id} view={view} className={styles.heroSlide} />
              ))}
            </div>
          ) : null}
        </section>

        {/* UI-DESIGN: LB-46 what a host gets: the case file opened on the
            cleared table. LB-58 (Avi): it comes straight after the hero,
            before How, so what you receive is clear before the catalogue.
            The three points are the product as the data model has it: a
            character per guest (roles with passports and relationships),
            story updates every round (round_updates), and guests
            questioning each other (interrogation_tokens). DRAFT for Avi:
            how questioning works and whether updates arrive as texts. */}
        <section
          id="inside"
          className={styles.insideChapter}
          data-lb-chapter="box"
          data-lb-active={chapter === 1 ? "" : undefined}
          aria-labelledby="lb-inside-title"
        >
          <div className={styles.chapterPin} data-lb-pin="">
            <GlassPanel className={clsx(styles.card, styles.insideCard)}>
              <h2 id="lb-inside-title" className={styles.title}>
                What do you get?
              </h2>
              {/* UI-DESIGN: LB-77 Taco's heading and line, word for word */}
              <p className={styles.sub}>A murder mystery that you play on your phone with your friends, with everything you need in one place.</p>
              <ul className={styles.gets}>
                {GETS.map((item, index) => (
                  <li key={item.title} className={styles.get}>
                    {directionStudy && <span className={styles.featureMark} aria-hidden>
                      {index === 0 ? <Persons width={24} height={24} /> : index === 1 ? <Magnifier width={24} height={24} /> : <Comments width={24} height={24} />}
                    </span>}
                    <h3 className={styles.getTitle}>{item.title}</h3>
                    <p className={styles.getText}>{item.text}</p>
                  </li>
                ))}
              </ul>
            </GlassPanel>
            <div className={styles.insideStage}>
              <CaseFileBox />
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className={styles.howChapter}
          data-lb-chapter="how"
          data-case-id={HOW_CASE.id}
          data-lb-active={chapter === 2 ? "" : undefined}
          aria-labelledby="lb-how-title"
        >
          <div className={styles.chapterPin} data-lb-pin="">
            <div className={styles.howStage}>
              <SlideMount view={HOW_CASE} className={styles.howMount} />
            </div>
            <GlassPanel className={clsx(styles.card, styles.howCard)}>
              <h2 id="lb-how-title" className={styles.title}>
                How it works
              </h2>
              <ol className={styles.steps}>
                {STEPS.map((step, i) => (
                  <li key={step.title} className={styles.step}>
                    <span className={styles.stepNo} aria-hidden>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className={styles.stepTitle}>{step.title}</h3>
                    <p className={styles.stepText}>{step.text}</p>
                  </li>
                ))}
              </ol>
              {/* UI-DESIGN: LB-48 the same secondary glass button as the hero */}
             
            </GlassPanel>
          </div>
        </section>

        {/* UI-DESIGN: LB-59 (Avi) the three cases together in one chapter,
            then Host. Status lines stay ahead of it, in reading order. */}
        {isError ? (
          <div className={styles.note}>
            <p role="status">These are preview cases. Reload live cases to start hosting.</p>
            <LightboxButton isPending={isFetching} onPress={() => { void refetch(); }}>
              Reload cases
            </LightboxButton>
          </div>
        ) : null}
        {isLoading ? (
          <p className={styles.note} role="status" aria-busy="true">
            Developing the slides…
          </p>
        ) : cases.length > 0 ? (
          <CaseSpread
            cases={cases}
            mode={mode}
            frames={(v) => frameFor(v.name)}
            selectedId={selectedId}
            active={chapter === CASES_CHAPTER}
            onPreview={setPreviewing}
            onHost={hostCase}
            canHost={!isError}
          />
        ) : (
          <p className={styles.note} role="status">
            No cases are published yet.
          </p>
        )}

        <section
          id="host"
          className={styles.hostChapter}
          data-lb-chapter="host"
          data-lb-active={chapter === CASES_CHAPTER + (cases.length ? 1 : 0) ? "" : undefined}
          aria-labelledby="lb-host-title"
        >
          <GlassPanel className={clsx(styles.card, styles.hostCard)}>
            <h2 id="lb-host-title" className={styles.title}>
              Ready to host?
            </h2>
            <p className={styles.sub}>We'll handle the mystery. You bring the friends (and the snacks).</p>
            <div className={styles.actions}>
              <LightboxButton
                tone="primary"
                icon={<Persons />}
                trailing={<ArrowRight />}
                onPress={() => navigate("/create-game")}
              >
                Host a party
              </LightboxButton>
              <LightboxButton tone="secondary" icon={<Magnifier />} onPress={() => navigate("/mysteries")}>
                Browse mysteries
              </LightboxButton>
            </div>
          </GlassPanel>
        </section>
      </main>
      {/* UI-DESIGN: LB-47 the rail is centred on the right edge, so growing
          from 4 entries to 7 when the cases land moved every entry; it comes
          in once, complete */}
      {mode === "three" && !isLoading ? <StoryRail chapters={rail} active={chapter} /> : null}
      {mode === "three" ? <Loupe view={hovered} /> : null}
      <RolePreview view={previewing} onClose={() => setPreviewing(null)} onHost={hostCase} canHost={!isError} />
      {reviewType ? <TypeReview value={typeface} onChange={chooseTypeface} /> : null}
    </LightboxSurface>
  );
};

// UI-DESIGN: a review-only entry renders real components without changing app routes.
const BrandSystem = lazy(() => import("../../components/brand/BrandSystem"));
const HomeEntry = () => new URLSearchParams(window.location.search).has("brand-system")
  ? <Suspense fallback={<p role="status">Opening the brand system…</p>}><BrandSystem /></Suspense>
  : <Home />;
export default HomeEntry;
